import React, { useState, useEffect, useRef } from 'react';
import ReactEcharts from 'echarts-for-react';
import axios from 'axios';
import styles from '../styles/Chart.module.css';

interface DataPoint {
  timestamp: string;
  value: number;
}

interface ScatterChartProps {
  timeframe: string;
}

const ScatterChart: React.FC<ScatterChartProps> = ({ timeframe }) => {
  const [data, setData] = useState<DataPoint[]>([]);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get<DataPoint[]>('data.json');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const aggregateDataByTimeframe = (): { xAxisData: string[], seriesData: { value: number[], itemStyle: object }[] } => {
    let xAxisData: string[] = [];
    let seriesData: { value: number[], itemStyle: object }[] = [];

    if (timeframe === 'daily') {
      seriesData = data.map(item => ({
        value: [new Date(item.timestamp).getTime(), item.value], // Convert timestamp to milliseconds or numeric representation
        itemStyle: {
          color: `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)})`, // Random color for each data point
        },
      }));
      xAxisData = data.map(item => item.timestamp);
    } else if (timeframe === 'weekly') {
      const weeklyDataMap = new Map<string, number>();
      data.forEach(item => {
        const weekOfYear = getWeekOfYear(item.timestamp);
        const key = `Week ${weekOfYear}`;
        if (weeklyDataMap.has(key)) {
          weeklyDataMap.set(key, weeklyDataMap.get(key)! + item.value);
        } else {
          weeklyDataMap.set(key, item.value);
        }
      });
      weeklyDataMap.forEach((value, key) => {
        seriesData.push({
          value: [parseInt(key.split(' ')[1]), value], // Extract year and convert to number
          itemStyle: {
            color: `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)})`, // Random color for each data point
          },
        });
        xAxisData.push(key);
      });
    } else if (timeframe === 'monthly') {
      const monthlyDataMap = new Map<string, number>();
      data.forEach(item => {
        const monthOfYear = new Date(item.timestamp).getMonth();
        const key = `${getMonthName(monthOfYear)} ${new Date(item.timestamp).getFullYear()}`;
        if (monthlyDataMap.has(key)) {
          monthlyDataMap.set(key, monthlyDataMap.get(key)! + item.value);
        } else {
          monthlyDataMap.set(key, item.value);
        }
      });
      monthlyDataMap.forEach((value, key) => {
        seriesData.push({
          value: [new Date(key).getTime(), value], // Convert month to milliseconds or numeric representation
          itemStyle: {
            color: `rgb(${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)}, ${Math.floor(Math.random() * 255)})`, // Random color for each data point
          },
        });
        xAxisData.push(key);
      });
    }

    return { xAxisData, seriesData };
  };

  const getWeekOfYear = (timestamp: string): string => {
    const date = new Date(timestamp);
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7).toString();
  };

  const getMonthName = (monthIndex: number): string => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthIndex];
  };

  const getOption = (): echarts.EChartsOption => ({
    xAxis: {
      type: 'time', // Use 'time' type for xAxis if using timestamp values
    },
    yAxis: {
      type: 'value',
    },
    series: [
      {
        symbolSize: 20,
        data: aggregateDataByTimeframe().seriesData,
        type: 'scatter',
      },
    ],
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const param = params[0];
        return `Date: ${new Date(param.data.value[0]).toLocaleDateString()}<br />Value: ${param.data.value[1]}`;
      },
    },
    dataZoom: [
      {
        type: 'inside',
        start: 0,
        end: 100,
      },
      {
        start: 0,
        end: 100,
      },
    ],
  });

  const handleChartClick = (params: any) => {
    alert(`Clicked: ${new Date(params.data.value[0]).toLocaleDateString()} - ${params.data.value[1]}`);
  };

  const handleExport = () => {
    const echartsInstance = chartRef.current.getEchartsInstance();
    const dataURL = echartsInstance.getDataURL({
      type: 'png',
      pixelRatio: 2,
      backgroundColor: '#fff',
    });

    const link = document.createElement('a');
    link.href = dataURL;
    link.download = 'scatter_chart.png';
    link.click();
  };

  return (
    <div className={styles.card}>
      <ReactEcharts
        ref={chartRef}
        option={getOption()}
        style={{ height: '300px', width: '100%' }}
        onEvents={{ click: handleChartClick }}
      />
      <button className={styles.chartButton} onClick={handleExport}>Export as PNG</button>
    </div>
  );
};

export default ScatterChart;
